import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";

/** Matches {@code com.collegenet.series25.document.mail.EmailMessageRecipient}. */
export interface EmailMessageRecipient {
    id?: number;
    email_message_id?: number;
    recipient_type?: number;
    recipient_address?: string;
    is_read?: boolean;
    read_at?: string;
    created_at?: string;
    cont_id_to?: number;
    contact_name_to?: string;
    delivery_status?: string;
}

/** Matches {@code com.collegenet.series25.document.mail.EmailMessage}. */
export interface EmailMessage {
    id?: number;
    convo_uuid?: string;
    from_address?: string;
    to_address?: string;
    subject?: string;
    body?: string;
    received_at?: string;
    direction?: "INBOUND" | "OUTBOUND";
    cont_id_from?: number;
    contact_name_from?: string;
    event_id?: number;
    acct_id?: number;
    email_message_recipient?: EmailMessageRecipient | EmailMessageRecipient[];
}

/** Matches {@code com.collegenet.series25.document.mail.EmailMessageConversation}. */
export interface EmailMessageConversation {
    convo_uuid?: string;
    message_count?: number;
    message_recipient_count?: number;
    message_sender_count?: number;
    message_unread_count?: number;
    message_read_count?: number;
    email_message?: EmailMessage[];
}

/** Matches {@code com.collegenet.series25.document.mail.EmailMessages} response attributes. */
export interface EmailMessagesResponse {
    conversation?: EmailMessageConversation[];
    paginate_key?: string;
    page_num?: string;
    page_count?: string;
    total_results?: string;
}

/** Raw JSON from POST {@code /email_messages/query.json} (wrapped or flat). */
type EmailMessagesQueryWireResponse = { email_messages?: EmailMessagesResponse } | EmailMessagesResponse;

/** POST body filters; {@code is_read} values must be {@code "T"} or {@code "F"}. */
export interface EmailMessageQuery {
    cont_id?: string[];
    cont_id_to?: string[];
    cont_id_from?: string[];
    is_read?: string[];
    search_term?: string[];
    search_term_body?: string[];
    search_term_subject?: string[];
    created_dt_start?: string;
    created_dt_end?: string;
    convo_uuid?: string[];
    organization_id?: string[];
    event_id?: string[];
}

const SERVICE = "EmailMessageService";

function normalizeConversation(rawConversation: EmailMessageConversation): EmailMessageConversation {
    return {
        ...rawConversation,
        email_message: S25Util.array.forceArray(rawConversation?.email_message),
    };
}

function sortMessagesNewestFirst(messages: EmailMessage[]): EmailMessage[] {
    return messages.slice().sort((left, right) => {
        const leftTime = new Date(left.received_at ?? 0).getTime();
        const rightTime = new Date(right.received_at ?? 0).getTime();
        return rightTime - leftTime;
    });
}

function unwrapEmailMessagesQueryResponse(response: EmailMessagesQueryWireResponse): EmailMessagesResponse {
    if ("email_messages" in response && response.email_messages != null) {
        return response.email_messages;
    }

    return response as EmailMessagesResponse;
}

export class EmailMessageService {
    public static queryMessages(
        query: EmailMessageQuery,
        page: number = 1,
        paginateKey?: string,
        clearCache: boolean = false,
    ): Promise<EmailMessagesResponse> {
        const queryString = DataAccess.params({
            paginate: paginateKey ?? "T",
            page,
            ...(clearCache ? { clear_cache: "true" } : {}),
        });
        const url = `/email_messages/query.json?${queryString}`;
        return DataAccess.post<EmailMessagesQueryWireResponse>(
            DataAccess.injectCaller(url, `${SERVICE}.queryMessages`),
            {
                email_message_query: query,
            },
        ).then((response): EmailMessagesResponse => {
            const payload = unwrapEmailMessagesQueryResponse(response);
            const conversations = S25Util.array.forceArray(payload?.conversation).map(normalizeConversation);
            return {
                conversation: conversations,
                paginate_key: payload?.paginate_key,
                page_num: payload?.page_num,
                page_count: payload?.page_count,
                total_results: payload?.total_results,
            };
        });
    }

    /**
     * Loads one conversation thread. Call once per thread open — do not invoke in loops.
     * Pagination is irrelevant when {@code convo_uuid} uniquely identifies the thread.
     */
    public static async getThread(
        convoUuid: string,
        contactId?: number,
    ): Promise<EmailMessageConversation | undefined> {
        const query: EmailMessageQuery = {
            convo_uuid: [convoUuid],
        };
        if (!S25Util.isUndefined(contactId)) {
            query.cont_id = [String(contactId)];
        }

        const response = await EmailMessageService.queryMessages(query);
        const conversation = response.conversation?.[0];
        if (!conversation) {
            return undefined;
        }

        return {
            ...conversation,
            email_message: sortMessagesNewestFirst(conversation.email_message ?? []),
        };
    }

    public static markRead(recipientIds: number[], isRead: boolean = true): Promise<void> {
        const url = DataAccess.injectCaller("/email_messages/read.json", `${SERVICE}.markRead`);
        return DataAccess.put(url, {
            email_message_read_update: {
                is_read: isRead,
                email_message_recipient_id: recipientIds,
            },
        });
    }
}
